import { useKohde, useKohdeSynkka } from "./Kohdekonteksti";
import { useLataus, LatausTila } from "./Lataus";
import { EiKohteita } from "./EiKohteita";
import { Ohje } from "./Ohje";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { naytaPvm } from "./pvm";
import { LinjaLinkki, saaAvata } from "./Linkit";
import { nayttonimi, nayttonimiPieni, selite } from "./nayttonimi";
import { api, ApiError, type Asettelu, type Ennuste, type Kayttaja, type Kohde, type Nakyma, type ProjektiHub, VAIHEET, VAIHEEN_NIMI } from "./api";
import { TILAVARI, Tilasana } from "./Tila";
import { t, virheTeksti, type KaannosAvain } from "./kieli";
import { KAANNOKSET } from "./kaannokset";
import { TARJOUSKORTIN_ROOLIT } from "./shared/types";

/* Projekti-Hub: kohteen koko tila yhdellä sivulla, koostettuna olemassa
   olevasta datasta (hitsausloki, NDT, punchit, miehitys, tarjous). */

const eur = (n: number) => n.toLocaleString("fi-FI", { maximumFractionDigits: 0 }) + " €";

/* -- VÄRI VAHVISTAA SANAN, EI KORVAA SITÄ --------------------
   Paletin oma perustelu (tyyli.css): `--ok` ja `--varoitus` eivät
   eroa luminanssiltaan lainkaan, ja asiakkaan `--korostus` osuu
   niiden päälle. Siksi jokaisessa sirussa on SANA, ja väri vain
   vahvistaa sen. Ruudun on oltava luettava mustavalkoisena.

   Tila on kannassa viisi arvoa; kapasiteetille niistä on merkitystä
   yhdellä bitillä (`f_kohde_varaa`: vain `aktiivinen` varaa).
   Erottelun ainoa lukija on ihminen -- siksi tiloja on viisi eikä
   kaksi, ja siksi siru kertoo myös kapasiteettiseurauksen. */
const TILAT: Record<string, { sana: KaannosAvain; vari: string; varaa: boolean }> = {
  aktiivinen:    { sana: "hub.tila.aktiivinen",    vari: TILAVARI.kunnossa, varaa: true  },
  keskeytetty:   { sana: "hub.tila.keskeytetty",   vari: "var(--varoitus)", varaa: false },
  peruuntunut:   { sana: "hub.tila.peruuntunut",   vari: "var(--puna)",     varaa: false },
  valmis:        { sana: "hub.tila.valmis",        vari: "var(--muste)",    varaa: false },
  suunnitteilla: { sana: "hub.tila.suunnitteilla", vari: "var(--himmea)",   varaa: false },
};

function Siru({ sana, vari, taytto }: { sana: string; vari: string; taytto?: boolean }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", fontSize: 11, fontWeight: 800,
      letterSpacing: "0.06em", lineHeight: 1.6, whiteSpace: "nowrap",
      color: taytto ? "#fff" : vari,
      background: taytto ? vari : "transparent",
      border: `2px solid ${vari}`,
    }}>{sana}</span>
  );
}

/* Pinottu palkki. Osat ovat SAMAA yksikköä -- sekayksikkö palkissa on
   sama virhe kuin sekayksikkö luvussa, mutta näkymättömämpi. */
function Palkki({ osat, korkeus = 10 }: { osat: { n: number; vari: string }[]; korkeus?: number }) {
  const koko = osat.reduce((a, o) => a + o.n, 0);
  if (koko <= 0) return <div style={{ height: korkeus, background: "var(--viiva)" }} />;
  return (
    <div style={{ height: korkeus, display: "flex", background: "var(--viiva)" }}>
      {osat.map((o, i) => o.n > 0 ? (
        <div key={i} style={{ width: `${(100 * o.n) / koko}%`, background: o.vari }} />
      ) : null)}
    </div>
  );
}
const num = (n: number) => n.toLocaleString("fi-FI", { maximumFractionDigits: 1 });

/* ═══ JOKAINEN LUKU RUUDULLA ON AVATTAVISSA RIVEIKSI ═══════════
   -- tai se ei kuulu ruudulle.

   Luku Hubissa on väite joukosta: "48 avointa punchia". Väite jota
   ei voi avata riveiksi on väite jota ei voi tarkistaa, ja luku
   jota ei voi tarkistaa luetaan mittauksena vaikka se olisi
   laskettu eri ehdolla kuin lista jonka lukija seuraavaksi avaa.

   <Luku> on <a>, jonka osoite on kohdelistan osoite SAMALLA
   suodattimella (osoitesääntö, main.tsx), ja jonka data-attribuutit
   ovat kävelytarkistuksen (`deploy/kavely.mjs`) sopimus:

     data-luku    luku sellaisenaan (kokonaisluku, ei muotoiltu)
     data-joukko  kohdelistan tunniste; lista merkitsee oman
                  rivimääränsä `data-rivit` + sama `data-joukko`
     data-nimi    ihmisluettava nimi raporttiin

   Kävely klikkaa jokaisen, lukee kohdenäkymän `data-rivit`in ja
   kaatuu jos luvut eroavat. Sama SQL-ehto on palvelimen puolella
   (routes.ts, PUNCH_AVOIN / PUUTE_JONOSSA / dokumentointivelkaRivit);
   tämä on se portti joka huomaa kun ehdot ajautuvat erilleen. */
function Luku({ n, joukko, nimi, selite, osoite, tyyli }: {
  /** `nimi` on kävelyn raporttinimi (data-nimi, ei käännetä); `selite` on käyttäjän kielellä title-tekstiin. */
  n: number; joukko: string; nimi: string; selite?: string; osoite: string; tyyli?: CSSProperties;
}) {
  // 10.10.2026 Toni: tilaajalla luku ei linkitä suljettuun näkymään (Selailu, Puutteet, …) — arvo tekstinä.
  if (!saaAvata(osoite)) return <span style={tyyli} data-luku-suljettu={n} data-joukko={joukko}>{n}</span>;
  return (
    <a className="hub__linkki" href={osoite} style={tyyli}
       data-luku={n} data-joukko={joukko} data-nimi={nimi}
       title={t("hub.avaa", { nimi: selite ?? nimi })}>{n}</a>
  );
}

/* ═══ HUBIN IKKUNAT: roolin oletus + henkilökohtainen poikkeama ═══
   Järjestys ja piilotus tulevat palvelimelta (`/api/nakyma/hub`,
   api/src/nakyma.ts) jo ratkaistuna ja moduulisuodatettuna. Tämä
   komponentti ei päätä mitään: se piirtää kortit siinä järjestyksessä
   ja jättää piilotetut piirtämättä. Järjestys toteutetaan gridin
   `order`-ominaisuudella, jolloin kortin JSX ei liiku — kävelyn
   `a[data-luku]`-sopimus ei muutu.

   ASETTELU EI HAE DATAA. Kortin data on edelleen `/api/hub`in ja
   `/api/ennuste`n lohkoja omien porttiensa takana; rajattu käyttäjä
   näkee samat rivit asettelusta riippumatta, ja se mitataan. */
export interface HubAsettelu {
  muokkaus: boolean;
  jarj: (avain: string) => number;
  nayta: (avain: string) => boolean;
  kahva: (avain: string) => JSX.Element | null;
}

export function Hub({ kayttaja: _kayttaja, osoiteKohde, asetaOsoiteKohde }: {
  kayttaja: Kayttaja;
  /** Osoitteen `#/hub/<kohteen tunnus>` -hanta, tai null. */
  osoiteKohde?: string | null;
  asetaOsoiteKohde?: (tunnus: string | null) => void;
}) {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [valittu, setValittu] = useState<number | "">("");
  const [data, setData] = useState<ProjektiHub | null>(null);
  /* Valmistumisennuste on oma reittinsä (`/api/ennuste/:id`), koska se
     lukee koko kohteen rivit ja kalenterin — Hubin muu sisältö ei saa
     odottaa sitä. Virhe → kortti ei piirry; ei nollaa. */
  const [ennuste, setEnnuste] = useState<Ennuste | null>(null);
  /* Asettelu: palvelimen ratkaisema. Muokkaustilassa luonnos elää
     selaimessa ja TALLENNUS ON OMA NAPPINSA — vahinkoveto ei ole pysyvä. */
  const [nakyma, setNakyma] = useState<Nakyma | null>(null);
  const [muokkaus, setMuokkaus] = useState(false);
  const [luonnos, setLuonnos] = useState<Asettelu>({ piilotetut: [], jarjestys: [] });
  const [tallennusTaso, setTallennusTaso] = useState<string>("oma");
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const lataaNakyma = () => api.nakyma("hub").then(setNakyma).catch(() => setNakyma(null));
  useEffect(() => { lataaNakyma(); }, []);
  /* Jaettu linkki ei saa pudota hiljaa (sama sääntö kuin Selailussa):
     tuntematon tunnus osoitteessa on NÄKYVÄ virhe, ei oletuskohde. */
  const [osoiteVirhe, setOsoiteVirhe] = useState<string | null>(null);

  const klKohteet = useLataus();
  useEffect(() => {
    klKohteet.seuraa(api.kohteet().then((k) => {
      /* -- OLETUS VALITAAN, EI OTETA ENSIMMÄISENÄ --------------
         Aiemmin tässä oli `k[0]`, eli listan järjestys päätti mihin
         Hub avautuu. Demossa se osui kohteeseen jolla on 0 linjaa ja
         0 hitsiä, ja koko ruutu oli nollia -- ensivaikutelma
         tuotteesta jonka koko pointti on ettei nollaa näytetä
         mittauksena.

         Järjestys on sisällön mukaan laskeva. Ei kovakoodattua
         tunnusta: sama sääntö toimii jokaisessa instanssissa, ja
         kohde jolla on eniten mitattavaa on se jonka avaaminen
         kertoo eniten. Tasapelissä alkuperäinen järjestys pitää.

         Kun moduulia ei ole, `linjoja` puuttuu -- silloin ei
         lajitella lainkaan, koska poissaolo ei ole nolla. */
      const mitattu = k.some((x) => typeof x.linjoja === "number");
      const jarjestys = mitattu
        ? k.map((x, i) => [x, i] as const)
           .sort((a, b) => (b[0].linjoja ?? 0) - (a[0].linjoja ?? 0) || a[1] - b[1])
           .map(([x]) => x)
        : k;
      setKohteet(jarjestys);
    }));
  }, [klKohteet.kerta]);

  /* Kohde osoitteesta, tai oletus. Ajetaan kun lista on ladattu ja
     aina kun osoite muuttuu (takaisin-nappi, liitetty linkki).
     Oletus kirjoitetaan osoitteeseen, jotta F5 ja jaettu linkki
     avaavat saman kohteen eikä "sen jolla on eniten linjoja juuri
     nyt". */
  const oletusAsetettu = useRef(false);
  // T426: oletus kirjoitetaan polkuun, mutta se ei ole «osoitteen kohde» — muuten yläpalkki saisi Hubin arvauksen.
  const oletusTunnus = useRef<string | null>(null);
  const kohdeKtx = useKohde();
  useEffect(() => {
    if (kohteet.length === 0) return;
    // Odotetaan yläpalkin valintaa, jotta oletus ei ehdi ennen sitä (ladattu-tila on undefined).
    if (kohdeKtx.kaytossa && kohdeKtx.valittu === undefined) return;
    if (osoiteKohde) {
      const k = kohteet.find((x) => x.tunnus === osoiteKohde);
      if (k) { setOsoiteVirhe(null); setValittu(k.id); return; }
      setOsoiteVirhe(t("hub.kohdetta_ei_loydy", { tunnus: osoiteKohde }));
      setValittu("");
      return;
    }
    setOsoiteVirhe(null);
    // Oletus vain kerran: jos käyttäjä itse valitsee "Valitse kohde…",
    // tyhjä osoite ei saa napata häntä takaisin oletukseen.
    if (oletusAsetettu.current) return;
    oletusAsetettu.current = true;
    // T426: yläpalkin kohde on oletus kun se on valittu; muuten Hubin oma (eniten linjoja).
    // 278: arkistoitu kohde ei ole oletus; osoitteen tunnus avaa sen silti (yllä).
    const oletus = (kohdeKtx.valittu != null && kohteet.find((x) => x.id === kohdeKtx.valittu))
      || kohteet.find((x) => !x.arkistoitu) || kohteet[0]!;
    oletusTunnus.current = oletus.tunnus;
    setValittu(oletus.id);
    asetaOsoiteKohde?.(oletus.tunnus);
  }, [kohteet, osoiteKohde, kohdeKtx.kaytossa, kohdeKtx.valittu]);

  const valitse = (id: number | "") => {
    setValittu(id);
    const k = kohteet.find((x) => x.id === id);
    asetaOsoiteKohde?.(k ? k.tunnus : null);
  };
  // T426: yläpalkin kohde ohjaa; polun #/hub/<tunnus> kohde vaihtaa yläpalkin (ratkeaa vasta kun lista on ladattu).
  // «Kaikki kohteet» ei kelpaa Hubille — silloin Hub pitää oman oletuksensa eikä työnnä sitä yläpalkkiin.
  const polunKohde = osoiteKohde && osoiteKohde !== oletusTunnus.current
    ? kohteet.find((x) => x.tunnus === osoiteKohde)?.id : undefined;
  const { ylapalkissa, ylaValittu } = useKohdeSynkka(valittu, valitse,
    { osoitteenKohde: polunKohde, kaikkiSallittu: false, valmis: kohteet.length > 0 });

  useEffect(() => {
    if (valittu === "") { setData(null); return; }
    api.hub(valittu).then(setData).catch(() => setData(null));
    setEnnuste(null);
    api.ennuste(valittu).then(setEnnuste).catch(() => setEnnuste(null));
  }, [valittu]);

  return (
    <>
      <header className="head">
        <div className="head__rivi" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <h1>{t("valikko.projekti_hub")}</h1>
          {nakyma && (
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
              {!muokkaus ? (
                <>
                  <span className="dim" >
                    {nakyma.taso === "oma" ? t("hub.asettelu.oma") : nakyma.taso === "rooli" ? t("hub.asettelu.rooli", { rooli: nayttonimiPieni("rooli", nakyma.rooli) }) : t("hub.asettelu.oletus")}
                  </span>
                  <button className="nappi nappi--toissijainen" onClick={() => {
                    setLuonnos({ piilotetut: nakyma.kortit.filter((k) => k.piilotettu).map((k) => k.avain), jarjestys: nakyma.kortit.map((k) => k.avain) });
                    setTallennusTaso("oma"); setIlmoitus(null); setMuokkaus(true);
                  }}>{t("hub.muokkaa_nakymaa")}</button>
                </>
              ) : (
                <>
                  {/* Pääkäyttäjälle sama työkalu, tallennus kysyy: omaksi vai roolin oletukseksi. */}
                  {nakyma.roolit && (
                    <select value={tallennusTaso} onChange={(e) => setTallennusTaso(e.target.value)} style={{ fontSize: 13 }}>
                      <option value="oma">{t("hub.tallenna_omaksi")}</option>
                      {nakyma.roolit.map((r) => <option key={r.tunnus} value={"rooli:" + r.tunnus}>{t("hub.tallenna_rooliin", { rooli: r.nimi })}{r.asetettu ? t("hub.asetettu") : ""}</option>)}
                    </select>
                  )}
                  <button className="nappi" onClick={async () => {
                    try {
                      if (tallennusTaso.startsWith("rooli:")) await api.tallennaRoolinNakyma("hub", tallennusTaso.slice(6), luonnos);
                      else await api.tallennaNakyma("hub", luonnos);
                      setMuokkaus(false); setIlmoitus(null); await lataaNakyma();
                    } catch (e) { setIlmoitus(t("hub.tallennus_epaonnistui", { virhe: e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : String(e) })); }
                  }}>{t("kuittaus.tallenna")}</button>
                  {/* Palauta oletus on näkyvissä, ei valikon takana. */}
                  <button className="nappi nappi--toissijainen" onClick={async () => {
                    try {
                      if (tallennusTaso.startsWith("rooli:")) await api.palautaRoolinNakyma("hub", tallennusTaso.slice(6));
                      else await api.palautaNakyma("hub");
                      setMuokkaus(false); setIlmoitus(null); await lataaNakyma();
                    } catch (e) { setIlmoitus(t("hub.palautus_epaonnistui", { virhe: e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : String(e) })); }
                  }}>{t("hub.palauta_oletus")}</button>
                  <button className="nappi nappi--toissijainen" onClick={() => { setMuokkaus(false); setIlmoitus(null); }}>{t("hl.peruuta")}</button>
                </>
              )}
            </div>
          )}
        </div>
        {ilmoitus && <div style={{ color: "var(--puna)", fontSize: 13, marginTop: 4 }}>{ilmoitus}</div>}
        {/* Raja 3: tyhjä Hub ei ole sallittu lopputila — palvelin palautti
            oletuksen ja tämä rivi sanoo miksi. */}
        {nakyma?.tyhja && !muokkaus && (
          <div style={{ fontSize: 13, marginTop: 4, color: "var(--varoitus)" }}>
            {t("hub.tyhja_asettelu")}
          </div>
        )}
        {muokkaus && (
          <div className="dim" style={{ marginTop: 4 }}>
            {t("hub.muokkaustila")}
          </div>
        )}
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />
      <EiKohteita haettu={klKohteet.tila === "valmis"} kohteita={kohteet.length} />

      {/* Ohje ylös (Toni 24.9.2026). Lähde: /api/hub/:kohde_id: vaadi + kohderajaus, lohkot moduuleittain (loki, valmius,
          kirjaukset hitsausloki; miehitys ja tekijät ei tilaajalle). Tarjouskortin roolit luetaan samasta vakiosta kuin
          palvelimen ehto (TARJOUSKORTIN_ROOLIT, Toni 10.10.2026 «Hub-korjaukset»: teksti oli jäänyt 04f0a36:n taakse). */}
      <Ohje
        kuka={<>{t("hub.ohje.kuka", { roolit: TARJOUSKORTIN_ROOLIT.map((r) => nayttonimi("rooli", r).toLowerCase()).join(t("hub.ja")) })}</>}
        luvut={<>{t("hub.ohje.luvut")}</>} />

      {/* data-sisalto: kävelyn taitemittaus (25.9.2026) — kohteen valinta on ensimmäinen sisältö, kortit sen alla. */}
      <div style={{ margin: "0 0 14px" }} data-sisalto="">
        {/* T539 (Toni 12.10.2026 «siiistimpi alku»): «Kohde: … (valinta yläpalkissa)» ja «KOHDE» pois — kohde näkyy yläpalkissa
            ja Perustiedot-kortissa. Valitsin jää näkyviin vain kun yläpalkissa ei ole kohdevalintaa, ja silloin sillä on näkyvä
            otsikko (lomakekenttien sääntö 11.10.2026: kenttä ilman otsikkoa on ✗; pekilon kävely de2d404). */}
        <label hidden={ylapalkissa && ylaValittu != null} data-hub-kohdevalitsin="">{t("hub.kohde_iso")}{" "}
        <select value={valittu} onChange={(e) => valitse(e.target.value ? Number(e.target.value) : "")}
                data-kohde={kohteet.find((k) => k.id === valittu)?.tunnus ?? ""}>
          <option value="">{t("hub.valitse_kohde")}</option>
          {/* Tyhjää ei piiloteta valitsimesta -- kohde joka on
              olemassa mutta jota ei voi valita on hämmentävämpi kuin
              kohde joka kertoo olevansa tyhjä. Merkintä on mitattu
              luku, ei arvio. */}
          {/* 278: arkistoitu kohde ei ole valitsimessa, paitsi kun se on avattu (osoitteesta tai Kohteet-näkymästä). */}
          {kohteet.filter((k) => !k.arkistoitu || k.id === valittu).map((k) => (
            <option key={k.id} value={k.id}>
              {k.tunnus} · {k.nimi}
              {k.linjoja === 0 ? t("hub.ei_linjoja_suluissa") : ""}
            </option>
          ))}
        </select></label>
      </div>

      {osoiteVirhe && <p className="huom huom--vika" role="alert">{osoiteVirhe}</p>}
      {!data?.kohde && !osoiteVirhe
        && <p className="dim">{t("hub.valitse_kohde_ohje")}</p>}
      {data?.kohde && (() => {
        /* Ratkaistu järjestys palvelimelta; muokkaustilassa luonnos.
           Kortti jota palvelin ei listaa (moduuli ei käytössä) ei näy
           kummassakaan — vanha tallennettu rivi ei ole lupa. */
        const sallitut = nakyma ? nakyma.kortit.map((k) => k.avain) : null;
        const jarjestys = muokkaus ? luonnos.jarjestys : (nakyma?.kortit.map((k) => k.avain) ?? []);
        const piilotetut = new Set(muokkaus ? luonnos.piilotetut : (nakyma?.kortit.filter((k) => k.piilotettu).map((k) => k.avain) ?? []));
        const siirra = (avain: string, suunta: -1 | 1) => setLuonnos((l) => {
          const j = [...l.jarjestys]; const i = j.indexOf(avain); const tk = i + suunta;
          if (i < 0 || tk < 0 || tk >= j.length) return l;
          [j[i], j[tk]] = [j[tk]!, j[i]!]; return { ...l, jarjestys: j };
        });
        const vaihdaPiilo = (avain: string) => setLuonnos((l) => ({
          ...l, piilotetut: l.piilotetut.includes(avain) ? l.piilotetut.filter((x) => x !== avain) : [...l.piilotetut, avain],
        }));
        const asettelu: HubAsettelu = {
          muokkaus,
          jarj: (avain) => { const i = jarjestys.indexOf(avain); return i < 0 ? 999 : i + 1; },
          nayta: (avain) => (sallitut === null || sallitut.includes(avain)) && (muokkaus || !piilotetut.has(avain)),
          kahva: (avain) => !muokkaus ? null : (
            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center", marginBottom: 6, fontSize: 12 }}>
              {piilotetut.has(avain) && <span style={{ fontWeight: 800, letterSpacing: "0.06em", color: "var(--himmea)", marginRight: "auto" }}>{t("hub.piilotettu")}</span>}
              <button className="nappi nappi--toissijainen" title={t("hub.siirra_ylos")} onClick={() => siirra(avain, -1)}>▲</button>
              <button className="nappi nappi--toissijainen" title={t("hub.siirra_alas")} onClick={() => siirra(avain, 1)}>▼</button>
              <button className="nappi nappi--toissijainen" title={piilotetut.has(avain) ? t("hub.nayta_kortti") : t("hub.piilota_kortti")} onClick={() => vaihdaPiilo(avain)}>
                {piilotetut.has(avain) ? t("hub.nayta") : t("hub.piilota")}
              </button>
            </div>
          ),
        };
        return <HubSisalto data={data} ennuste={ennuste} asettelu={asettelu}
                           asiakas={_kayttaja.laji === "ulkopuolinen" && !_kayttaja.roolit.includes("tarkastaja")} />;
      })()}
    </>
  );
}

function HubSisalto({ data, ennuste, asettelu, asiakas = false }: { data: ProjektiHub; ennuste: Ennuste | null; asettelu: HubAsettelu; asiakas?: boolean }) {
  const k = data.kohde!;
  const { jarj, nayta, kahva } = asettelu;
  /** Toni 25.10.2026 klo 12.50: avatut pysäyttävien ryhmät (avain tyyppi|sääntö|vastuun laji). */
  const [jonoAuki, setJonoAuki] = useState<Set<string>>(new Set());
  /* Piilotettu kortti muokkaustilassa: näkyy himmeänä, jotta sen voi
     tuoda takaisin. Muuten sitä ei renderöidä lainkaan. */
  const korttiTyyli = (avain: string, lisa: CSSProperties = {}): CSSProperties =>
    ({ ...kortti, ...lisa, order: jarj(avain), opacity: asettelu.muokkaus && !nayta(avain) ? 0.45 : undefined });
  // Hub on kooste useasta moduulista, ja palvelin jättää lohkon pois
  // jos moduulia ei ole. Poissaolo ei ole nolla: korttia ei piirretä
  // lainkaan, jottei asiakas jolla ei ole hitsauslokia näkisi "0 %"
  // etenemää projektista jossa ei hitsata.
  const l = data.loki;

  /* -- MODUULI PÄÄLLÄ EI TARKOITA ETTÄ MITATTAVAA ON ---------
     Palvelin jättää lohkon pois jos MODUULIA ei ole; se on
     moduuliportti eikä saa venyä muuksi. Mutta kohteella voi olla
     hitsausloki päällä ja NOLLA linjaa -- demossa viidellä
     yhdeksästä on juuri niin -- ja silloin etenemä, NDT ja
     luovutusvalmius piirtyivät muodossa `0 / 0` viidellä rivillä.
     Se näyttää siltä että kaikki on kunnossa.

     Nolla nimittäjä ei ole mittaustulos. Kortteja ei piirretä. */
  const lokiaOn = !!l && l.linjoja > 0;

  /* Yksi desimaali, ei kokonaisluku.
   *
   * N-2410:ssä kolme eri suuretta pyöristyi samaan lukuun: etenemä
   * 56,7 % (dia-inch), NDT-kattavuus 56,9 % ja hitsejä tehty 55,8 %.
   * Ruudulla ne olivat kaikki "57 %", ja katsoja päättelee kolmesta
   * samasta luvusta että ne mittaavat samaa asiaa. Ne eivät mittaa.
   *
   * Ja nolla nimittäjä on VIIVA, ei nolla — sama sääntö kuin
   * `prosentti()`-apurissa: 0 % on mittaustulos, – on tiedon puute. */
  const pros = (osa: number, koko: number): number | null => (koko > 0 ? (100 * osa) / koko : null);
  const osuus = l ? pros(l.dia_inch_tehty, l.dia_inch_tavoite) : null;
  /* NDT-nimittäjä on HITSATUT, ei kaikki hitsirivit: suunniteltua saumaa
     ei voi tarkastaa, ja pekilossa 380 suunniteltua painoi osuutta alas
     (4.9.2026: 4 338 / 10 364 → / 9 984). Nimittäjä sanotaan luvun vieressä. */
  const ndtOsuus = l ? pros(l.ndt_hitseja, l.hitsattu) : null;
  const pr = (v: number | null) => (v === null ? "—" : num(v) + " %");

  /* Kohdelistojen osoitteet. Parametrit ovat API:n omat, jotta osoite
     on luettavissa suoraan kutsuksi:
       punchit   GET /api/punchit?kohde=&vain_avoimet=1
       linjat    GET /api/linjat?kohde_id=      (Selailun kohdesuodatin)
       puutteet  GET /api/puutteet?kohde_id=&tila=avoimet&tyyppi=&vaikutus=
       aukot     GET /api/dokumentointivelka?kohde=  + selaimen `ulottuvuus`
     `ulottuvuus` ei ole API-parametri: dokumentointivelka palauttaa
     kaikki rivit ja luku johdetaan niistä samalla säännöllä kuin
     täällä (`!aloittamatta && <ulottuvuus>`), joten lista suodattaa
     saman rivijoukon selaimessa. */
  const osoite = {
    punchit:  `#/selailu?nayta=punchit&kohde=${k.id}&vain_avoimet=1`,
    vahvistettavat: `#/selailu?kohde=${k.id}&nayta=vahvistettavat`,
    linjat:   `#/selailu?kohde=${k.id}`,
    puutteet: (tyyppi: string, vaikutus: string, saanto?: string, vastuu?: string) =>
      `#/puutteet?kohde_id=${k.id}&tila=avoimet&tyyppi=${encodeURIComponent(tyyppi)}`
      + `&vaikutus=${encodeURIComponent(vaikutus)}`
      + (saanto ? `&saanto=${encodeURIComponent(saanto)}` : "") + (vastuu ? `&vastuu=${encodeURIComponent(vastuu)}` : ""),
    aukot:    (ulottuvuus: string) => `#/aukot?kohde=${k.id}&ulottuvuus=${ulottuvuus}`,
    vaihe:    (vaihe: string) => `#/aukot?kohde=${k.id}&vaihe=${vaihe}`,
  };

  const kortti: CSSProperties = { background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 14 };
  const iso: CSSProperties = { fontSize: 30, fontWeight: 800, lineHeight: 1 };
  /* Kortin otsikko: eyebrow on sivun tason merkki (10 px, harmaa) eikä
     erotu kortin sisällöstä — Markku K. 2.9.2026. Nostetaan kokoa ja
     kontrastia VAIN täällä; globaali .eyebrow jää ennalleen. */
  const otsikko: CSSProperties = {
    fontSize: 12, color: "var(--muste)", letterSpacing: "0.12em",
    margin: "0 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--viiva)",
  };

  return (
    /* data-sisalto: kävelyn ohjekorkeuden väite mittaa, alkaako ensimmäinen kortti taitteen yläpuolella (25.9.2026). */
    <div data-sisalto="" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 12 }}>
      {nayta("perustiedot") && (<div style={korttiTyyli("perustiedot")}>
        {kahva("perustiedot")}
        <h3 className="eyebrow" style={otsikko}>{t("hub.perustiedot")}</h3>
        <div style={{ fontWeight: 800, fontSize: 18 }}>{k.tunnus}</div>
        <div>{k.nimi}</div>
        <div className="dim" style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7 }}>
          {k.asiakas && <div>{t("hub.asiakas", { asiakas: k.asiakas })}</div>}
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {(() => {
            const tila = TILAT[k.tila];
            const sana = tila ? t(tila.sana) : k.tila.toUpperCase();
            const vari = tila?.vari ?? "var(--himmea)";
            return <>
              <Siru sana={sana} vari={vari} taytto />
              {/* Kapasiteettiseuraus sanotaan ääneen: se on se mitä
                  työnjohtaja oikeasti kysyy tilaa vaihtaessaan, ja se
                  oli tähän asti näkymätön joka näkymässä. */}
              {!tila?.varaa && <Siru sana={t("hub.ei_varaa")} vari="var(--himmea)" />}
            </>;
          })()}
          <span className="dim" title={selite("kohde_laji", k.laji)}>{nayttonimi("kohde_laji", k.laji)}</span>
        </div>
        <div className="dim" style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7 }}>
          {(k.alkaa || naytaPvm(k.paattyy)) && <div>{t("hub.kesto", { alku: naytaPvm(k.alkaa) ?? "?", loppu: naytaPvm(k.paattyy) ?? "?" })}</div>}
          {k.tyonumero && <div>{t("hub.tyonro", { nro: k.tyonumero })}</div>}
        </div>
      </div>)}

      {/* Moduuli on päällä mutta kohteella ei ole yhtään linjaa.
          Yksi lause on parempi kuin neljä korttia joissa lukee 0/0:
          "ei mitattavissa" on tulos, "0" on väite. */}
      {nayta("etenema") && l && !lokiaOn && (
        <div style={korttiTyyli("etenema", { borderLeft: "3px solid var(--himmea)" })}>
          {kahva("etenema")}
          <h3 className="eyebrow" style={otsikko}>{t("valikko.hitsausloki")}</h3>
          <Siru sana={t("hub.ei_mitattavissa_iso")} vari="var(--himmea)" />
          <div className="dim" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
            {t("hub.ei_linjoja_selite")}
          </div>
        </div>
      )}

      {nayta("etenema") && lokiaOn && l && (
        <div style={korttiTyyli("etenema")}>
          {kahva("etenema")}
          <h3 className="eyebrow" style={otsikko}>{t("hub.etenema_otsikko")}</h3>
          <div style={iso}>{pr(osuus)}</div>
          <div className="dim" style={{ fontSize: 13 }}>{t("hub.dia_inch_suhde", { tehty: num(l.dia_inch_tehty), tavoite: num(l.dia_inch_tavoite) })}</div>
          <div style={{ height: 8, background: "var(--sinkki)", marginTop: 10 }}>
            <div style={{ height: "100%", width: `${Math.min(osuus ?? 0, 100)}%`, background: "var(--korostus)" }} />
          </div>
          {/* Vaiheakseli: hitsattu + suunniteltu = hitseja, ja se summautuu.
              Aiemmin tässä luki `tehty` (paiva IS NOT NULL) suunnitellun
              rinnalla — kaksi eri akselia vierekkäin, jolloin
              1 508 + 467 = 1 975 eikä 2 704 ja 729 hitsiä putosi
              lukujen väliin näkymättömiin. */}
          <div className="dim" style={{ marginTop: 10, fontSize: 13 }}>
            <Luku n={l.linjoja} joukko="linjat" nimi="linjaa" selite={t("hub.linjaa")} osoite={osoite.linjat} />
            {" "}{t("hub.linjaa_hitsattu", { n: l.linjoja, hitsattu: l.hitsattu, hitseja: l.hitseja })}
            {l.suunniteltu > 0 ? t("hub.suunniteltu_lisa", { n: l.suunniteltu }) : ""}
          </div>
          {/* Toinen palkki, ERI YKSIKKÖ ja se sanotaan: yllä dia-inch,
              tässä saumojen lukumäärä. Sekayksikkö palkissa on sama
              virhe kuin sekayksikkö luvussa mutta näkymättömämpi.

              Kolme segmenttiä, koska päiväämätön hitsattu on oma
              tilansa: työ on tehty mutta se ei ole aikajanalla. Ilman
              tätä se katoaa "tehdyn" sisään eikä kukaan korjaa sitä. */}
          <Palkki osat={[
            { n: l.hitsattu - l.hitsattu_paivaton, vari: TILAVARI.kunnossa },
            { n: l.hitsattu_paivaton,              vari: "var(--varoitus)" },
            { n: l.suunniteltu,                    vari: "var(--viiva)" },
          ]} />
          <div className="dim" style={{ marginTop: 4, lineHeight: 1.7 }}>
            <span style={{ color: TILAVARI.kunnossa, fontWeight: 700 }}>■</span> {t("hub.paivatty", { n: l.hitsattu - l.hitsattu_paivaton })}
            {l.hitsattu_paivaton > 0 ? <>
              {" · "}<span style={{ color: "var(--varoitus)", fontWeight: 700 }}>■</span> {t("hub.paivaamaton", { n: l.hitsattu_paivaton })}
            </> : null}
            {l.suunniteltu > 0 ? <>{" · "}■ {t("hub.suunniteltu_n", { n: l.suunniteltu })}</> : null}
            {t("hub.saumoja")}
          </div>
          {l.hitsattu_paivaton > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--varoitus)" }}>
              ⚠ {t("hub.paivattomat_varoitus", { n: l.hitsattu_paivaton })}
            </div>
          )}
          {/* 194 (päätös 62 kohta 5): työnjohdon jono — vahvistamattomat lisähitsit ja «ei toteudu» -esitykset. */}
          {(l.vahvistusta_odottaa ?? 0) > 0 && (
            <div style={{ marginTop: 8, fontSize: 13, color: "var(--varoitus)" }} data-hub-vahvistettavat={l.vahvistusta_odottaa}>
              <Luku n={l.vahvistusta_odottaa} joukko="vahvistettavat" nimi="muutosta odottaa vahvistusta" selite={t("hub.odottaa_vahvistusta")} osoite={osoite.vahvistettavat} />
              {" "}{t("hub.odottaa_vahvistusta", { n: l.vahvistusta_odottaa })}
            </div>
          )}
        </div>
      )}

      {nayta("laatu") && lokiaOn && l && (
        <div style={korttiTyyli("laatu")}>
          {kahva("laatu")}
          <h3 className="eyebrow" style={otsikko}>{t("hub.laatu")}</h3>
          <div style={{ display: "flex", gap: 20 }}>
            <div>
              <div style={iso}>{pr(ndtOsuus)}</div>
              <div className="dim" >{t("hub.ndt_hitsatusta", { n: l.ndt_hitseja, m: l.hitsattu })}</div>
            </div>
            <div>
              <div style={{ ...iso, color: l.avoimia_puncheja ? TILAVARI.vika : TILAVARI.kunnossa }}>
                <Luku n={l.avoimia_puncheja} joukko="punchit" nimi="avointa punchia" selite={t("hub.avointa_punchia")}
                      osoite={osoite.punchit} />
              </div>
              <div className="dim" >{t("hub.avointa_punchia", { n: l.avoimia_puncheja })}</div>
            </div>
          </div>
          {l.hylatty > 0 && (
            <div style={{ marginTop: 10, color: "var(--puna)", fontSize: 13 }}>
              ⚠ {t("hub.rt_hylatty", { n: l.hylatty })}
            </div>
          )}
        </div>
      )}

      {/* Päätös 51 (Toni 2.10.2026 «Hub pitää elää noille eri moduuleille»): moduulin rivi näkyy vain kun palvelin
          antoi lohkon — eli moduuli on päällä JA koskee kohdetta (työlaji 188 tai dataa). Poissaolo ei ole nolla.
          Sekakohde saa useamman rivin; yhteistä valmiuslukua ei lasketa ennen Tonin painotusta. */}
      {nayta("laiteasennus") && data.laite && (
        <div style={korttiTyyli("laiteasennus")} data-hub-lohko="laite">
          {kahva("laiteasennus")}
          <h3 className="eyebrow" style={otsikko}>{t("valikko.laiteasennus")}</h3>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <div><div style={iso}>{data.laite.laitteita}</div><div className="dim">{t("hub.laitetta")}</div></div>
            <div><div style={iso}>{data.laite.vaiheita_kuitattu}/{data.laite.vaiheita}</div><div className="dim">{t("hub.vaihetta_kuitattu")}</div></div>
            <div><div style={iso}>{data.laite.liitoksia_kuitattu}/{data.laite.liitoksia}</div><div className="dim">{t("hub.liitosta_kuitattu")}</div></div>
          </div>
        </div>
      )}
      {nayta("iv_asennus") && data.iv && (
        <div style={korttiTyyli("iv_asennus")} data-hub-lohko="iv" data-hub-iv-valmius={data.iv.valmius ?? ""}>
          {kahva("iv_asennus")}
          <h3 className="eyebrow" style={otsikko}>{t("valikko.iv_asennus")}</h3>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <div><div style={iso}>{data.iv.valmius == null ? "—" : num(data.iv.valmius) + " %"}</div><div className="dim">{t("hub.iv.valmius")}</div></div>
            <div><div style={iso}>{data.iv.jarjestelmia}</div><div className="dim">{t("hub.iv.jarjestelmaa")}</div></div>
            <div><div style={iso}>{data.iv.osuuksia_ok}/{data.iv.osuuksia}</div><div className="dim">{t("hub.iv.osuuksia", { n: data.iv.osuuksia_mitattu })}</div></div>
            <div><div style={iso}>{data.iv.paatteita_ok}/{data.iv.paatteita}</div><div className="dim">{t("hub.iv.paatteita", { n: data.iv.paatteita_mitattu })}</div></div>
          </div>
          {data.iv.osuuksia === 0 && <div className="dim" style={{ marginTop: 8 }}>{t("hub.iv.ei_osuuksia")}</div>}
        </div>
      )}
      {nayta("miehitys") && (data.miehitys !== undefined || data.tekijat !== undefined)
        && (<div style={korttiTyyli("miehitys")}>
        {kahva("miehitys")}
        <h3 className="eyebrow" style={otsikko}>{t("hub.miehitys")}</h3>
        {(() => {
          const mieh = data.miehitys ?? [];
          const tek = data.tekijat ?? [];
          if (mieh.length > 0) {
            return <>
              {mieh.slice(0, 10).map((m) => (
                <div key={m.resurssi_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span>{m.lyhenne ? m.lyhenne + " · " : ""}{m.nimi}</span>
                  <span className="mono dim">{num(m.tunnit)} h</span>
                </div>
              ))}
              {mieh.length > 10 && <div className="dim" style={{ marginTop: 4 }}>{t("hub.n_muuta", { n: mieh.length - 10 })}</div>}
            </>;
          }
          if (tek.length > 0) {
            return <>
              <div className="dim" style={{ marginBottom: 4 }}>{t("hub.tyopaketeista")}</div>
              {/* PÄÄTTYNYT TEKIJÄ MERKITÄÄN, EI SUODATETA POIS.
                  Mitattu 2.9.2026: kortti listasi 11 tekijää joista 3
                  oli lopettanut, ja he olivat riveillä 2–4
                  merkitsemättöminä. Kortin nimi on MIEHITYS ja se
                  vastaa kysymykseen "kuka tekee tätä" — lähtenyt
                  ihminen siinä listassa vastaa väärin.

                  Suodatus olisi väärä korjaus: työpaketin tekijä­
                  historia on totta, ja jos rivi katoaa, työ näyttää
                  tehdyn itsestään. Sama peruste kuin holdatun kohteen
                  allokoinnilla (088) — näkyy, merkittynä. */}
              {tek.slice(0, 10).map((tk) => (
                <div key={tk.resurssi_id} style={{ display: "flex", justifyContent: "space-between",
                                                 fontSize: 13, opacity: tk.paatti ? 0.65 : 1 }}>
                  <span>
                    {tk.lyhenne ? tk.lyhenne + " · " : ""}{tk.nimi}
                    {tk.paatti && (
                      <span style={{ marginLeft: 6, padding: "0 5px", fontSize: 10, fontWeight: 800,
                                     letterSpacing: "0.04em", color: "var(--himmea)",
                                     border: "1px solid var(--himmea)", whiteSpace: "nowrap" }}>
                        {t("hub.paattynyt")}
                      </span>
                    )}
                  </span>
                  <span className="mono dim">{t("hub.pkt", { n: tk.paketteja })}</span>
                </div>
              ))}
              {tek.length > 10 && <div className="dim" style={{ marginTop: 4 }}>{t("hub.n_muuta", { n: tek.length - 10 })}</div>}
            </>;
          }
          // Työpaketit ovat hitsauslokin osa: ilman sitä palvelin ei
          // palauta tekijoita lainkaan, eikä niihin pidä ohjata.
          return <div className="dim">
            {data.tekijat ? t("hub.ei_miehitysta_tp") : t("hub.ei_miehitysta")}
          </div>;
        })()}
      </div>)}

      {nayta("tarjoukset") && data.tarjoukset && data.tarjoukset.length > 0 && (
        <div style={korttiTyyli("tarjoukset")}>
          {kahva("tarjoukset")}
          <h3 className="eyebrow" style={otsikko}>{t("valikko.tarjoukset")}</h3>
          {data.tarjoukset.map((tj) => (
            <div key={tj.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span>{t("hub.tarjous_nro", { id: tj.id })}</span>
              <span className="mono">{eur(tj.hinta)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── ALARIVI ────────────────────────────────────────────
          Kolme lohkoa samassa ruudukossa: se on `auto-fit`, joten ne
          asettuvat riviksi ylärivin alle ilman omaa asettelua.

          Jokainen piirretään vain jos palvelin palautti sen. Puuttuva
          lohko EI ole nolla: asiakas jolla ei ole puutemoduulia ei saa
          nähdä "0 avointa puutetta". */}

      {nayta("puutteet") && data.puutteet && (
        <div style={korttiTyyli("puutteet")}>
          {kahva("puutteet")}
          <h3 className="eyebrow" style={otsikko}>{t("hub.puutteet_luokittain")}</h3>
          {/* T567 (Toni 13.10.2026 «GO»): jono — pysäyttävät ensin riveinä, muut ryhmiteltyinä tyypin ja säännön mukaan (määrä +
              avaa lista), ulkoisen vastuun ryhmät omana lohkonaan. Ryhmän luku avaa Puutteet-listan samalla suodattimella
              (sääntö, vaikutus, vastuun laji) — luku ja lista samasta SQL-ehdosta. Vanha tyyppi × vaikutus -lista jää
              alle (yhteensä), eikä sitä nollata. */}
          {data.puutejono && (() => {
            const j = data.puutejono; const py = j.pysayttavat; const yht = py[0]?.yhteensa ?? 0;
            const sisaiset = j.ryhmat.filter((r) => !r.vastuu_ulkoinen); const ulkoiset = j.ryhmat.filter((r) => r.vastuu_ulkoinen);
            const odottajat = [...new Set(ulkoiset.map((r) => r.odottaa ?? "?"))];
            // 13.10.2026 (demon kävely 19b7ec2: «sulatus puuttuu» suomeksi englanninkielisessä valikossa): säännön nimi avaimesta;
            // tuntematon sääntö (uusi 022:n tunniste) näkyy tunnuksena, ei kaadu.
            const saantoNimi = (s: string) => {
              if (s === "kasin") return t("hub.jono.kasin");
              const avain = `hub.saanto.${s}`;
              return avain in KAANNOKSET ? t(avain as KaannosAvain) : s.replace(/_/g, " ");
            };
            return (
              <div data-hub-jono="" style={{ fontSize: 13, marginBottom: 10 }}>
                <div style={{ fontWeight: 700, margin: "4px 0" }} data-hub-jono-pysayttaa={yht}>
                  <Siru sana={t("hub.siru.pysayttaa")} vari="var(--puna)" taytto /> {t("hub.jono.pysayttaa_nyt", { n: yht })}
                  {yht > py.length && <span className="dim"> · {t("hub.jono.naytetaan", { n: py.length })}</span>}
                </div>
                {py.length === 0 && <div className="dim">{t("hub.jono.ei_pysayttavia")}</div>}
                {(() => {
                  const rivi = (p: (typeof py)[number]) => (
                    <div key={p.id} data-hub-jono-rivi={p.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0", borderBottom: "1px solid var(--viiva)" }}>
                      <span style={{ minWidth: 0 }}>
                        {p.linja_tunnus ? <LinjaLinkki tunnus={p.linja_tunnus} /> : <span className="dim">{nayttonimiPieni("kohde_taulu", p.kohde_taulu)}</span>}
                        {" · "}{saantoNimi(p.saanto)}
                        {p.vastuu_ulkoinen && <span className="dim"> · {t("hub.jono.odottaa", { kuka: p.vastuu ?? "?" })}</span>}
                      </span>
                      <span className="mono dim" style={{ whiteSpace: "nowrap" }}>{t("pu.vrk", { n: p.ika_vrk })}</span>
                    </div>
                  );
                  // Vanha palvelin ei palauta ryhmiä: rivit kuten ennen.
                  if (!j.pysayttavat_ryhmat) return py.map(rivi);
                  // Toni 25.10.2026 klo 12.50: yksi rivi ryhmää kohden määrineen; enintään viiden ryhmä näytetään suoraan auki,
                  // isompi avautuu painamalla 10 vanhimpaan ja «Näytä kaikki N» vie Puutteet-listaan samalla suodattimella.
                  return j.pysayttavat_ryhmat.map((r) => {
                    const avain = `${r.tyyppi}|${r.saanto}|${r.vastuu_ulkoinen ? "ulkoinen" : "sisainen"}`;
                    const omat = py.filter((p) => p.tyyppi === r.tyyppi && p.saanto === r.saanto && p.vastuu_ulkoinen === r.vastuu_ulkoinen);
                    const pieni = r.n <= 5;
                    const auki = pieni || jonoAuki.has(avain);
                    return (
                      <div key={avain} data-hub-jono-ryhma={avain} data-hub-jono-ryhma-n={r.n} data-hub-jono-ryhma-auki={auki ? "1" : "0"}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
                          {pieni ? (
                            <span style={{ minWidth: 0 }}>{nayttonimi("puute_tyyppi", r.tyyppi)} · {saantoNimi(r.saanto)}
                              {r.vastuu_ulkoinen && <span className="dim"> · {t("hub.jono.odottaa", { kuka: r.odottaa ?? "?" })}</span>}</span>
                          ) : (
                            <button type="button" data-hub-jono-ryhma-avaa={avain} aria-expanded={auki}
                                    style={{ minWidth: 0, textAlign: "left", background: "none", border: 0, padding: 0, font: "inherit",
                                             color: "inherit", cursor: "pointer" }}
                                    onClick={() => setJonoAuki((s) => { const u = new Set(s); if (u.has(avain)) u.delete(avain); else u.add(avain); return u; })}>
                              {auki ? "▾ " : "▸ "}{nayttonimi("puute_tyyppi", r.tyyppi)} · {saantoNimi(r.saanto)}
                              {r.vastuu_ulkoinen && <span className="dim"> · {t("hub.jono.odottaa", { kuka: r.odottaa ?? "?" })}</span>}
                              <span className="dim"> · {t("hub.jono.vanhin", { n: r.vanhin_vrk })}</span>
                            </button>
                          )}
                          <span className="mono">
                            <Luku n={r.n} joukko="puutteet" nimi={`${r.tyyppi} · ${r.saanto} · pysayttaa · ${r.vastuu_ulkoinen ? "ulkoinen" : "sisainen"}`}
                                  selite={`${nayttonimi("puute_tyyppi", r.tyyppi)} · ${saantoNimi(r.saanto)}`}
                                  osoite={osoite.puutteet(r.tyyppi, "pysayttaa", r.saanto, r.vastuu_ulkoinen ? "ulkoinen" : "sisainen")} />
                          </span>
                        </div>
                        {auki && (
                          <div style={{ paddingLeft: pieni ? 0 : 14 }}>
                            {(pieni ? omat : omat.slice(0, 10)).map(rivi)}
                            {!pieni && r.n > 10 && (
                              <a className="dim" data-hub-jono-ryhma-kaikki={avain}
                                 href={osoite.puutteet(r.tyyppi, "pysayttaa", r.saanto, r.vastuu_ulkoinen ? "ulkoinen" : "sisainen")}>
                                {t("hub.jono.nayta_kaikki", { n: r.n })} ›</a>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
                {sisaiset.length > 0 && <div style={{ fontWeight: 700, margin: "10px 0 2px" }}>{t("hub.jono.muut")}</div>}
                {sisaiset.map((r) => (
                  <div key={`s-${r.tyyppi}-${r.saanto}-${r.vaikutus}`} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                      {r.vaikutus === "hidastaa" && <Siru sana={t("hub.siru.hidastaa")} vari="var(--varoitus)" />}
                      {nayttonimi("puute_tyyppi", r.tyyppi)} · {saantoNimi(r.saanto)}
                      <span className="dim">· {t("hub.jono.vanhin", { n: r.vanhin_vrk })}</span>
                    </span>
                    <span className="mono">
                      <Luku n={r.n} joukko="puutteet" nimi={`${r.tyyppi} · ${r.saanto} · ${r.vaikutus} · sisainen`}
                            selite={`${nayttonimi("puute_tyyppi", r.tyyppi)} · ${saantoNimi(r.saanto)}`}
                            osoite={osoite.puutteet(r.tyyppi, r.vaikutus, r.saanto, "sisainen")} />
                    </span>
                  </div>
                ))}
                {odottajat.map((kuka) => (
                  <div key={`u-${kuka}`} data-hub-jono-odottaa={kuka}>
                    <div style={{ fontWeight: 700, margin: "10px 0 2px" }}>{t("hub.jono.odottaa_muita", { kuka })}</div>
                    {ulkoiset.filter((r) => (r.odottaa ?? "?") === kuka).map((r) => (
                      <div key={`u-${r.tyyppi}-${r.saanto}-${r.vaikutus}`} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <span style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                          {r.vaikutus === "hidastaa" && <Siru sana={t("hub.siru.hidastaa")} vari="var(--varoitus)" />}
                          {nayttonimi("puute_tyyppi", r.tyyppi)} · {saantoNimi(r.saanto)}
                          <span className="dim">· {t("hub.jono.vanhin", { n: r.vanhin_vrk })}</span>
                        </span>
                        <span className="mono">
                          <Luku n={r.n} joukko="puutteet" nimi={`${r.tyyppi} · ${r.saanto} · ${r.vaikutus} · ulkoinen`}
                                selite={`${nayttonimi("puute_tyyppi", r.tyyppi)} · ${saantoNimi(r.saanto)}`}
                                osoite={osoite.puutteet(r.tyyppi, r.vaikutus, r.saanto, "ulkoinen")} />
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
                <div className="dim" style={{ fontWeight: 700, margin: "10px 0 2px" }}>{t("hub.jono.yhteensa")}</div>
              </div>
            );
          })()}
          {data.puutteet.length === 0
            ? <div className="dim" style={{ fontSize: 13 }}>{t("hub.ei_avoimia_puutteita")}</div>
            : data.puutteet.map((p) => (
                <div key={p.tyyppi + p.vaikutus}
                     style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {/* Vaikutus on kolmiportainen eikä boolean. Siru
                        kantaa SANAN; väri vain vahvistaa sen. */}
                    {p.vaikutus === "pysayttaa" && <Siru sana={t("hub.siru.pysayttaa")} vari="var(--puna)" taytto />}
                    {p.vaikutus === "hidastaa"  && <Siru sana={t("hub.siru.hidastaa")}  vari="var(--varoitus)" />}
                    {nayttonimi("puute_tyyppi", p.tyyppi)}
                  </span>
                  <span className="mono">
                    <Luku n={p.n} joukko="puutteet" nimi={`${nayttonimi("puute_tyyppi", p.tyyppi)} · ${nayttonimiPieni("puute_vaikutus", p.vaikutus)}`}
                          osoite={osoite.puutteet(p.tyyppi, p.vaikutus)} />
                  </span>
                </div>
              ))}
        </div>
      )}

      {nayta("luovutusvalmius") && lokiaOn && data.valmius && (
        <div style={korttiTyyli("luovutusvalmius")}>
          {kahva("luovutusvalmius")}
          <h3 className="eyebrow" style={otsikko}>{t("valikko.luovutusvalmius")}</h3>
          <div style={iso}>
            {data.valmius.keskivalmius_aloitetut === null
              ? "—" : data.valmius.keskivalmius_aloitetut + " %"}
          </div>
          {/* Kaksi lukua, ei yhtä: "miten aloitetut etenevät" ja
              "moniko on aloittamatta" ovat eri kysymyksiä. Yksi
              keskiarvo vastaa toiseen ja valehtelee toisesta. */}
          <div className="dim" style={{ fontSize: 13 }}>
            <Luku n={data.valmius.aloitettuja} joukko="luovutusvalmius" nimi="aloitettua linjaa" selite={t("hub.luku.aloitettua")}
                  osoite={osoite.aukot("aloitettu")} />
            {" "}{t("hub.aloitetusta")} ·{" "}
            <Luku n={data.valmius.aloittamatta} joukko="luovutusvalmius" nimi="aloittamatonta linjaa" selite={t("hub.luku.aloittamatonta")}
                  osoite={osoite.aukot("aloittamatta")} />
            {" "}{t("hub.aloittamatta_suhde", { n: data.valmius.linjoja })}
          </div>
          {data.valmius.aloittamatta > 0 && (
            <div style={{ marginTop: 8 }}>
              <Siru sana={t("hub.aukkoa", { n: data.valmius.aloittamatta })} vari="var(--himmea)" />
            </div>
          )}
          {/* T397 (Toni 30.9.2026): «Valmis seuraavaan vaiheeseen» — jokainen linja on täsmälleen yhdessä
              vaiheessa, joten summa on linjamäärä ja se sanotaan otsikossa (koettelu näkyvissä). Luku avautuu
              Luovutusvalmiuteen samalla säännöllä (`?vaihe=`), ja kävely vertaa lukua listaan. */}
          {data.valmius.vaiheet && (
            <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.9 }} data-vaiheet-summa={VAIHEET.reduce((s, v) => s + data.valmius!.vaiheet[v], 0)}>
              <div className="dim">{t("hub.seuraava_vaihe", { n: VAIHEET.reduce((s, v) => s + data.valmius!.vaiheet[v], 0), m: data.valmius.linjoja })}</div>
              {VAIHEET.map((v) => (
                <div key={v}>
                  <Luku n={data.valmius!.vaiheet[v]} joukko="luovutusvalmius" nimi={`seuraava vaihe: ${VAIHEEN_NIMI[v]}`}
                        selite={t("hub.luku.seuraava_vaihe", { vaihe: t(`hub.vaihe.${v}` as KaannosAvain) })}
                        osoite={osoite.vaihe(v)} />{" "}{t(`hub.vaihe.${v}` as KaannosAvain, { n: data.valmius!.vaiheet[v] })}
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.9 }}>
            {([
              ["loki", "Loki täydellinen", "hub.dim.loki"], ["ndt", "NDT tehty", "hub.dim.ndt"],
              ["painekoe", "Painekoe", "enn.vaihe.painekoe"], ["walkdown", "Walkdown hyväksytty", "hub.dim.walkdown"],
              ["ei_punchia", "Ei avoimia puncheja", "hub.dim.ei_punchia"],
            ] as const).map(([avain, nimi, nimiAvain]) => {
              const n = data.valmius!.dimensiot[avain];
              const koko = data.valmius!.aloitettuja;
              /* NELJÄ ERI NOLLAA, EI YHTÄ -- ja ilman uutta väriä:
                   koko = 0   ei mitattavaa      viiva, ei palkkia
                   n = 0      mitattu, ei yhtään puna
                   0 < n      kesken             varoitus
                   n = koko   valmis             ok
                 "0" ilman nimittäjää ei tarkoita mitään; `0 / 6`
                 tarkoittaa. Aiemmin tässä luki `0 / 0` viidesti ja
                 ruutu näytti siltä että kaikki on kunnossa. */
              const vari = koko === 0 ? "var(--viiva)"
                         : n === koko ? TILAVARI.kunnossa
                         : n === 0    ? TILAVARI.vika
                         :              TILAVARI.varoitus;
              return (
                <div key={avain} style={{ marginBottom: 5 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{t(nimiAvain)}</span>
                    <span className="mono dim">
                      {koko > 0
                        ? <><Luku n={n} joukko="luovutusvalmius" nimi={nimi} selite={t(nimiAvain)}
                                  osoite={osoite.aukot(avain)} /> / {koko}</>
                        : <Tilasana laji="ei_mitattu">{t("iv.ei_mitattavaa")}</Tilasana>}
                    </span>
                  </div>
                  {koko > 0
                    ? <Palkki korkeus={4} osat={[{ n, vari }, { n: koko - n, vari: "var(--viiva)" }]} />
                    : <div style={{ height: 4, background: "var(--viiva)" }} />}
                </div>
              );
            })}
            {/* T677 B (Toni 27.10.2026 klo 09.25): järjestysrike laatuhavaintona — «Painekoe ennen NDT:tä: N linjaa», luku avaa
                listan. Ei estä mitään. Tilaajalle neutraali sanamuoto. Näkyy vain kun rikkeitä on. */}
            {(data.valmius.painekoe_ennen_ndt ?? 0) > 0 && (() => {
              const n = data.valmius!.painekoe_ennen_ndt!;
              const nimiAvain = asiakas ? "hub.dim.painekoe_ennen_ndt_tilaaja" : "hub.dim.painekoe_ennen_ndt";
              return (
                <div style={{ marginTop: 6, borderLeft: "3px solid var(--varoitus)", paddingLeft: 8 }} data-hub-painekoe-ennen-ndt={n}>
                  ⚠ {t(nimiAvain)}:{" "}
                  <Luku n={n} joukko="luovutusvalmius" nimi="painekoe ennen NDT:tä" selite={t(nimiAvain)}
                        osoite={osoite.aukot("painekoe_ennen_ndt")} />{" "}{t("hub.linjaa_lyh", { n })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {nayta("ennuste") && lokiaOn && ennuste && (() => {
        /* ── VALMISTUMISENNUSTE (T229) ───────────────────────────
           Väli eikä päivä; peruste luvun vieressä; vaihe jota ei
           mitata on PUUTTUVA, ei valmis. Laskenta api/src/ennuste.ts,
           faktat /api/ennuste/:id. Jokainen jäljellä-luku on <Luku>
           samaan Luovutusvalmius-listaan (joukko <vaihe>_jaljella),
           ja kävely tarkistaa sen kuten muutkin. */
        const e = ennuste;
        const pvm = (p: string | null): string =>
          p === null ? t("hub.enn.ei_arvioitavissa") : p === "tänään" ? t("hub.enn.tanaan") : p.slice(8, 10).replace(/^0/, "") + "." + p.slice(5, 7).replace(/^0/, "") + "." + p.slice(0, 4);
        const des = (x: number | null): string => x === null ? "—" : x.toFixed(1).replace(".", ",");
        const vali = (a: string | null, b: string | null): string => a === null ? t("hub.enn.ei_arvioitavissa") : a === b ? pvm(a) : pvm(a) + " – " + pvm(b);
        const ikkuna = e.kalenteri.arkipaivia > 0 ? `${naytaPvm(e.kalenteri.ikkuna_alku)}–${naytaPvm(e.kalenteri.ikkuna_loppu)}` : t("hub.enn.ei_ikkunaa");
        /* Ensimmäinen rivi kertoo mitä järjestelmä TARVITSEE, ei vain mitä
           siltä puuttuu — sama lause, eri suunta (Toni 4.9.2026; Aki
           katsoo protottia ~17.9. ja tämä on ensimmäinen asia jonka
           ostaja näkee ennustekortista). Kaksi syytä eritellään, koska
           niillä on eri korjaus: «odottaa ensimmäisiä täyttymisiä» on
           työn asia, «tulleet erissä» on kirjaustavan asia. */
        const mitatut = e.vaiheet.filter((v) => v.mitattavissa && v.jaljella > 0);
        const mittaamattomat = e.vaiheet.filter((v) => !v.mitattavissa);
        /* Otsikko ei ole «ei mitattavissa» vaan mitatut välit ja nimetyt
           syyt vierekkäin — se on ero jonka ostaja näkee. */
        /* Toni 10.10.2026 «Hub-korjaukset»: «hitsaus: päivä puuttuu 10 täyttymältä» oli kömpelö ja katkesi kapealla
           näytöllä. Vaihe omalle rivilleen, nimi isolla, ja syy koodista käyttäjän kielellä («Hitsaus: ennuste puuttuu –
           10 valmiilta linjalta puuttuu päivä»). Vanha palvelin ilman koodia näyttää syy_lyhyt-tekstin kuten ennen. */
        const vaiheNimi = (v: string): string => t(`enn.vaihe.${v}` as KaannosAvain);
        const syyTeksti = (v: (typeof mittaamattomat)[number]): string =>
          v.syy_koodi ? t(`enn.syy.${v.syy_koodi}` as KaannosAvain, v.syy_arvot ?? {}) : (v.syy_lyhyt ?? t("enn.ei_mitattavissa"));
        const otsikko2 = e.yhteensa ? null : [
          ...mitatut.map((v) => `${vaiheNimi(v.vaihe)} ${vali(v.aikaisin, v.myohaisin)}`),
          ...mittaamattomat.map((v) => `${vaiheNimi(v.vaihe)}: ${syyTeksti(v)}`),
        ];
        const tarve = t("hub.enn.tarve", { n: mitatut.length });
        const tilat = e.nimittaja.tila.map((x) => `${nayttonimiPieni("linja_tila", x.tila)} ${x.n}`).join(" · ");
        const luokat = e.nimittaja.avoimet_punchit.map((x) => `${x.luokka ?? t("hub.enn.ilman_luokkaa")} ${x.n}`).join(" · ");
        const th: CSSProperties = { textAlign: "left", fontWeight: 600, color: "var(--himmea)", fontSize: 11, padding: "4px 8px 4px 0", borderBottom: "1px solid var(--viiva)" };
        const td: CSSProperties = { padding: "5px 8px 5px 0", verticalAlign: "top", borderBottom: "1px solid var(--viiva)" };
        /* ── KANNAKOINTI: KAKSI LAJIA, KETJUN KOHTA MITTAAMATTA (taso 1, 111) ──
           Primaari KOSKETTAA PUTKEA (pannat, sangat, liukukannakkeet,
           kiintopisteet) ja kantaa välittömän kuorman; sekundaari yhdistää
           primaarin rakennuksen runkoon (tangot, konsolit, kiskot). Se on
           määritelmä ja tarkistettavissa. Tässä luki 7.9.2026 asti «primaari
           ennen asennusta, sekundaari walkdownin jälkeen» — päätelmä sanasta
           primary, ei mittaus, ja se oli asiakkaan ruudulla kaksi päivää.
           Rivin sijainti ketjussa on yhtä lailla väite kuin sulkulauseke:
           molemmat rivit ovat ketjun ULKOPUOLELLA kunnes järjestys on
           mitattu sekundaariarkin asennuspäivistä hitsauspäiviä vasten.
           Luku on tilaajan tilannekuva linjaa kohti, EI
           tahti: prosentti ilman tapahtumapäiviä ei kelpaa tahdin
           lähteeksi, ja se sanotaan eri sanoin kuin «ei mitattu» —
           «tahti mitattavissa toisesta tuonnista». Tilaajan lukua ei
           lasketa uudelleen, Progress %-kaavaa ei toisteta. Ei tiedossa
           on kolmas tila (NULL), ei 0 %: neljäsosalla lähteen riveistä
           ei ole arvoa, ja 0 näyttäisi ne tekemättöminä. Jokainen luku
           avautuu Luovutusvalmius-listaan samalla joukolla. */
        const kk = e.kannakointi;
        const kannakeRivi = (laji: "primaari" | "sekundaari") => {
          const o = laji === "primaari" ? kk.primaari : kk.sekundaari;
          const nimi = laji === "primaari" ? "Primaarikannakointi" : "Sekundaarikannakointi";
          const nimiT = laji === "primaari" ? t("hub.enn.primaari") : t("hub.enn.sekundaari");
          const eiTietoa = o.tiedossa === 0;
          return (
            <tr key={"kannake-" + laji} style={{ background: "color-mix(in srgb, var(--pelti) 70%, var(--viiva))" }}>
              <td style={td}>{nimiT}<div className="dim" >{laji === "primaari" ? t("hub.enn.primaari_sel") : t("hub.enn.sekundaari_sel")}</div></td>
              <td style={{ ...td, whiteSpace: "nowrap" }} className="mono">
                {eiTietoa ? "—" : <><Luku n={o.jaljella} joukko="luovutusvalmius" nimi={`${nimi} — kesken (tilaajan %)`} selite={t("hub.enn.luku.kannake_kesken", { nimi: nimiT })} osoite={osoite.aukot(`${laji}_jaljella`)} /> {t("hub.linjaa", { n: o.jaljella })}</>}
              </td>
              <td style={td} className="mono">
                <span className="dim">{t("hub.enn.tilannekuva")}</span>
              </td>
              <td style={td}>
                {eiTietoa
                  ? <><span style={{ color: "var(--puna)", fontWeight: 700 }}>{t("hub.enn.ei_tiedossa")}</span><div className="dim" >{t("hub.enn.ei_tuotu")}</div></>
                  : <>
                      <span style={{ fontWeight: 700 }}>{t("hub.enn.valmiusaste_tiedossa")}</span>
                      <div className="dim" >
                        {t("hub.enn.tiedossa_rivi", { n: o.tiedossa, ka: des(o.keskiarvo), pvm: kk.tilannekuva ? pvm(kk.tilannekuva) : "—" })}
                        {kk.tahti_mitattavissa ? t("hub.enn.tahti_mitattavissa", { n: kk.linjoja_kahdella_tilannekuvalla }) : t("hub.enn.tahti_toisesta")}
                      </div>
                    </>}
              </td>
              <td style={td} className="dim">{t("hub.enn.ketjun_kohta")}</td>
              <td style={td} className="dim">
                {eiTietoa ? t("hub.enn.tuonti_progress") : t("hub.enn.toinen_tuonti")}
              </td>
            </tr>
          );
        };
        return (
          <div style={korttiTyyli("ennuste", { gridColumn: "1 / -1" })}>
            {kahva("ennuste")}
            <h3 className="eyebrow" style={otsikko}>{t("hub.enn.otsikko")}</h3>
            <div style={e.yhteensa ? iso : { fontSize: 16, fontWeight: 800, lineHeight: 1.4 }}>
              {e.yhteensa ? vali(e.yhteensa.aikaisin, e.yhteensa.myohaisin)
                : (otsikko2 ?? []).map((r) => <div key={r} data-ennuste-rivi="">{r}</div>)}
            </div>
            <div className="dim" style={{ fontSize: 13, marginTop: 4 }}>
              {e.yhteensa
                ? t("hub.enn.aikaisin_myohaisin")
                : tarve}
            </div>
            <div className="dim" style={{ marginTop: 2 }}>
              {t("hub.enn.tahti_peruste", { n: e.kalenteri.arkipaivia, ikkuna })}
              {!e.yhteensa && t("hub.enn.ei_kokonaisennustetta")}
            </div>
            <div className="scroll" style={{ marginTop: 10 }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5 }}>
                <thead><tr>
                  <th style={th}>{t("hub.enn.vaihe")}</th><th style={th}>{t("hub.enn.jaljella")}</th><th style={th}>{t("hub.enn.tahti")}</th>
                  <th style={th}>{t("hub.enn.valmis_oma")}</th><th style={th}>{t("hub.enn.ketjussa")}</th><th style={th}>{t("hub.enn.muuttaisi")}</th>
                </tr></thead>
                <tbody>
                  {e.vaiheet.map((v) => (
                    <tr key={v.vaihe}>
                      <td style={td}>{vaiheNimi(v.vaihe)}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }} className="mono">
                        <Luku n={v.jaljella} joukko="luovutusvalmius" nimi={`${v.nimi} — jäljellä`} selite={t("hub.enn.jaljella_selite", { vaihe: vaiheNimi(v.vaihe) })} osoite={osoite.aukot(v.joukko)} /> {t("hub.linjaa", { n: v.jaljella })}
                      </td>
                      <td style={td} className="mono">
                        {v.tahti.tahti === null ? "—" : t("hub.enn.tahti_arvo", { tahti: des(v.tahti.tahti) })}
                        <span className="dim"> ({t("hub.enn.tayttyi", { n: v.tahti.tayttyneita })}
                          {v.tahti.hitain5 !== null ? t("hub.enn.jaksot", { a: des(v.tahti.hitain5), b: des(v.tahti.nopein5) }) : ""}
                          {v.tahti.leikattu && v.tahti.tuonti_kattaa ? t("hub.enn.leikattu", { pvm: naytaPvm(v.tahti.tuonti_kattaa) ?? "" }) : ""})</span>
                        {v.paivaton > 0 && <div style={{ color: "var(--varoitus)", fontSize: 11 }}>⚠ {t("hub.enn.paivaton", { n: v.paivaton })}</div>}
                      </td>
                      <td style={td}>
                        {v.mitattavissa
                          ? <span style={{ fontWeight: v.jaljella === 0 ? 400 : 700 }}>{vali(v.aikaisin, v.myohaisin)}</span>
                          : <span style={{ color: "var(--puna)", fontWeight: 700 }}>{t("enn.ei_mitattavissa")}</span>}
                        {v.syy && <div className="dim" >{v.syy}</div>}
                        {v.mitattavissa && v.arkipaivia !== null && v.arkipaivia > 0 && <div className="dim" >{t("hub.enn.arkipaivaa", { n: v.arkipaivia })}</div>}
                      </td>
                      <td style={td}>{v.ketju_aikaisin === null ? <span className="dim">{t("hub.enn.ketju_katkennut")}</span> : vali(v.ketju_aikaisin, v.ketju_myohaisin)}</td>
                      <td style={td} className="dim">{v.muuttaisi}</td>
                    </tr>
                  ))}
                  {/* Kannakerivit viiden vaiheen JÄLKEEN, ketjun ulkopuolella:
                      niiden paikka ketjussa on mittaamatta, eikä paikka
                      taulukossa saa väittää sitä. */}
                  {kannakeRivi("primaari")}
                  {kannakeRivi("sekundaari")}
                </tbody>
              </table>
            </div>
            {/* Nimittäjä sanotaan siinä missä luku on: kolme eri
                nimittäjää (tilaajan lista, Sampon linjat, Hubin
                aloitetut) löydettiin 4.9.2026 ja löydettäisiin uudelleen
                kolmen kuukauden päästä ilman tätä riviä. */}
            <div className="dim" style={{ marginTop: 10, lineHeight: 1.7 }}>
              <b>{t("hub.enn.nimittaja")}</b> {t("hub.enn.nimittaja_rivi", { n: e.nimittaja.linjoja, a: e.nimittaja.aloitettu, b: e.nimittaja.aloittamatta, c: e.nimittaja.loki_taysi })}
              {" "}{t("hub.enn.jaljella_lasketaan", { w: e.nimittaja.aloittamattomilla.walkdown, p: e.nimittaja.aloittamattomilla.painekoe, ndt: e.nimittaja.aloittamattomilla.ndt })}
              {" "}<b>{t("hub.enn.rajaus")}</b> {t("hub.enn.linjan_tila", { tilat: tilat || "—" })}
              {e.nimittaja.tila.length === 1 && e.nimittaja.tila[0]?.tila === "kesken"
                ? t("hub.enn.kesken_selite")
                : "."}
              {" "}<b>{t("hub.enn.avoimet_punchit")}</b> {t("hub.enn.punch_luokat", { luokat: luokat || "0" })}
              {/* Kannakoinnin nimittäjä ja ristiriita samaan riviin: tilaajan
                  asennus 100 % ja sekundaari kesken näytetään nimettynä
                  lukuna, ei sulauteta (toimeksianto §4.1). */}
              {(kk.primaari.tiedossa > 0 || kk.sekundaari.tiedossa > 0) && (
                <>
                  {" "}<b>{t("hub.enn.kannakointi")}</b> {t("hub.enn.tilaajan_arvo", { n: kk.primaari.tiedossa })}{" "}
                  <Luku n={kk.ei_tiedossa} joukko="luovutusvalmius" nimi="kannakointi ei tiedossa" selite={t("hub.enn.luku.kannake_ei_tiedossa")} osoite={osoite.aukot("kannakointi_ei_tiedossa")} /> {t("hub.enn.ei_tiedossa")}
                  {" · "}{t("hub.enn.ristiriita")}{" "}
                  <Luku n={kk.ristiriita} joukko="luovutusvalmius" nimi="ristiriita: asennus 100 %, sekundaari kesken" selite={t("hub.enn.luku.ristiriita")} osoite={osoite.aukot("kannakointi_ristiriita")} />
                  {" "}{t("hub.enn.ristiriita_jatko", { lahde: kk.lahde ?? "—" })}
                </>
              )}
              {/* Suunnitelman kattavuus: mediaani 1 suunniteltu rivi per
                  aloittamaton linja tarkoittaa ettei isometrin hitsimäärä
                  ole Sampossa — «loki täydellinen» on silloin «kaikki
                  Sampon tuntemat rivit», ja hitsauksen jäljellä on alaraja.
                  Pekilo 4.9.2026: tilaajan listalla 134 «täydellistä» linjaa
                  on kesken (1 653 hitsiä). */}
              {e.nimittaja.suunniteltu_mediaani_aloittamaton !== null && e.nimittaja.suunniteltu_mediaani_aloittamaton <= 2 && (
                <div style={{ color: "var(--varoitus)", marginTop: 4 }}>
                  ⚠ {t("hub.enn.suunniteltu_varoitus", { n: e.nimittaja.suunniteltuja_hitseja, m: e.nimittaja.suunniteltu_mediaani_aloittamaton })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {nayta("kirjaukset") && lokiaOn && data.kirjaukset && (
        <div style={korttiTyyli("kirjaukset")}>
          {kahva("kirjaukset")}
          <h3 className="eyebrow" style={otsikko}>{t("hub.kirjaukset")}</h3>
          {data.kirjaukset.length === 0
            ? <div className="dim" style={{ fontSize: 13 }}>{t("hub.ei_kirjauksia")}</div>
            : data.kirjaukset.map((r, i) => (
                /* Linjatunnus on pitkä (`310-0071-AC-32-D25A1B-PP`) ja
                   söi koko rivin, jolloin itse TAPAHTUMA katkesi — eli
                   se ainoa asia jonka vuoksi rivi on olemassa. Nyt
                   tapahtuma saa tilan ja tunnus kutistuu; tunnus on
                   konteksti, tapahtuma on sisältö. */
                <div key={i} style={{ fontSize: 12, lineHeight: 1.8,
                                      display: "flex", gap: 8, alignItems: "baseline" }}>
                  <span className="mono dim" style={{ flex: "0 0 auto" }}>{naytaPvm(r.pvm)}</span>
                  <span style={{ flex: "1 1 auto", overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {/* Punchin teksti on lähteen tekstiä sellaisenaan (Progress-arkki, 9.10.2026: «… (NDT-lomake, 2026-09-30)»).
                        Merkintä kertoo kävelyn päiväyspyyhkäisylle, ettei sitä muotoilla eikä lueta Sampon esitykseksi. */}
                    {r.laji === "punch" ? <span data-lahdeteksti="">{r.teksti}</span> : r.teksti}{r.kuka ? " · " + r.kuka : ""}
                  </span>
                  <span className="dim" style={{ flex: "0 1 auto", minWidth: 0, maxWidth: "38%",
                                                 overflow: "hidden", textOverflow: "ellipsis",
                                                 whiteSpace: "nowrap" }}>
                    <LinjaLinkki tunnus={r.linja} />
                  </span>
                </div>
              ))}
          {/* Aikajana on PÄIVÄTTYJEN kirjausten, ei kaikkien: päivätön
              rivi ei voi asettua aikajanalle. Se ettei se näy tässä on
              sama tosiasia kuin etenemän ajoittamaton osuus. */}
        </div>
      )}
    </div>
  );
}
